import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Star, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Actualites', 'Formation', 'RH & Management', 'Bureautique',
  'Conseils', 'Polynésie', 'E-learning', 'Événements'
];

const emptyArticle = {
  title: '', excerpt: '', content: '', category: 'Actualites',
  tags: [], cover_image: '', published: false, featured: false
};

export const AdminBlogTab = ({ token }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyArticle });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog/articles`, { headers });
      setArticles(data);
    } catch {} finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyArticle });
    setTagInput('');
    setDialogOpen(true);
  };

  const openEdit = async (article) => {
    // Fetch full article with content
    try {
      const { data } = await axios.get(`${API}/blog/articles/${article.slug}`);
      setEditing(article.id);
      setForm({
        title: data.title || '',
        excerpt: data.excerpt || '',
        content: data.content || '',
        category: data.category || 'Actualites',
        tags: data.tags || [],
        cover_image: data.cover_image || '',
        published: data.published || false,
        featured: data.featured || false
      });
      setTagInput('');
      setDialogOpen(true);
    } catch {
      // Fallback: use partial data
      setEditing(article.id);
      setForm({
        title: article.title || '',
        excerpt: article.excerpt || '',
        content: '',
        category: article.category || 'Actualites',
        tags: article.tags || [],
        cover_image: article.cover_image || '',
        published: article.published || false,
        featured: article.featured || false
      });
      setDialogOpen(true);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) {
      setForm({ ...form, tags: [...form.tags, t] });
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/blog/articles/${editing}`, form, { headers });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/admin/blog/articles`, form, { headers });
        toast.success('Article créé');
      }
      setDialogOpen(false);
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API}/admin/blog/articles/${id}`, { headers });
      toast.success('Article supprimé');
      fetchArticles();
    } catch {
      toast.error('Erreur de suppression');
    }
  };

  const togglePublish = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/blog/articles/${id}/toggle-publish`, {}, { headers });
      toast.success(data.published ? 'Article publie' : 'Article depublie');
      fetchArticles();
    } catch {
      toast.error('Erreur');
    }
  };

  const filtered = articles.filter(a =>
    !searchFilter || a.title.toLowerCase().includes(searchFilter.toLowerCase())
  );

  return (
    <div data-testid="admin-blog-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Filtrer les articles..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="pl-10"
            data-testid="blog-admin-search"
          />
        </div>
        <Button onClick={openCreate} className="bg-[#0D9488] hover:bg-[#0F766E] text-white" data-testid="blog-create-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel article
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="animate-pulse space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg">Aucun article</p>
          <p className="text-sm mt-1">Cliquez sur "Nouvel article" pour commencer</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((article) => (
            <div
              key={article.id}
              className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
              data-testid={`blog-row-${article.id}`}
            >
              {/* Thumbnail */}
              <div className="w-16 h-12 rounded-md overflow-hidden flex-shrink-0 bg-gray-100">
                {article.cover_image ? (
                  <img src={article.cover_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-teal-500 to-teal-700" />
                )}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-[#1F2937] truncate">{article.title}</h4>
                  {article.featured && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                  <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">{article.category}</span>
                  <span>{new Date(article.created_at).toLocaleDateString('fr-FR')}</span>
                  <span>{article.views || 0} vues</span>
                </div>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost" size="icon"
                  onClick={() => togglePublish(article.id)}
                  title={article.published ? 'Depublier' : 'Publier'}
                  data-testid={`blog-toggle-${article.id}`}
                >
                  {article.published ? (
                    <Eye className="w-4 h-4 text-green-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  )}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(article)} data-testid={`blog-edit-${article.id}`}>
                  <Edit2 className="w-4 h-4 text-blue-500" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(article.id)} data-testid={`blog-delete-${article.id}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editing ? 'Modifier l\'article' : 'Nouvel article'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Titre de l'article"
                className="mt-1"
                data-testid="blog-form-title"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Categorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="blog-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Image de couverture (URL)</Label>
                <Input
                  value={form.cover_image}
                  onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                  placeholder="https://..."
                  className="mt-1"
                  data-testid="blog-form-image"
                />
              </div>
            </div>
            <div>
              <Label>Extrait (resume court)</Label>
              <Input
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="Resume en 1-2 phrases..."
                className="mt-1"
                data-testid="blog-form-excerpt"
              />
            </div>
            <div>
              <Label>Contenu (supporte Markdown: ## titres, **gras**, - listes, &gt; citations)</Label>
              <textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={12}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent resize-y"
                placeholder="Redigez votre article ici..."
                data-testid="blog-form-content"
              />
            </div>
            {/* Tags */}
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Ajouter un tag..."
                  className="flex-1"
                  data-testid="blog-form-tag-input"
                />
                <Button type="button" variant="outline" onClick={addTag} className="border-[#0D9488] text-[#0D9488]">
                  Ajouter
                </Button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1 text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded-full">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {/* Toggles */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#0D9488] focus:ring-[#0D9488]"
                  data-testid="blog-form-published"
                />
                <span className="text-sm text-gray-700">Publier immédiatement</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                  data-testid="blog-form-featured"
                />
                <span className="text-sm text-gray-700">Article à la une</span>
              </label>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white py-5"
              data-testid="blog-form-save"
            >
              {saving ? 'Enregistrement...' : (editing ? 'Mettre à jour' : 'Créer l\'article')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlogTab;
